import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBillingV1TaxCalculationUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/billing/v1/calculate-tax',
        destPath: '/v1/calculate-tax',
      },
      {
        srcPath: '/billing/v1/list-tax-calculators',
        destPath: '/v1/list-tax-calculators',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/billing/v1/calculate-tax',
        destPath: '/v1/calculate-tax',
      },
      {
        srcPath: '/billing/v1/list-tax-calculators',
        destPath: '/v1/list-tax-calculators',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_tax-calculation';

/**
 * Calculates tax for the provided line items.
 *
 * Tax is calculated for each line item based on the tax region
 * that corresponds to the address provided in `lineItems.addressIndex` and the tax group in `taxGroupId`. If no tax region is found for
 * the line item's address then no tax will be calculated for this line item. If no tax group with that `taxGroupId` is found then the
 * default tax group is used to calculate tax. Call List Default Tax Groups to retrieve a site's default tax groups.
 *
 * The tax is calculated by a tax calculator app installed on the site.
 * Call List Tax Calculators to see which tax calculators are available.
 * To provide your own tax calculations, use the Tax Calculation service plugin.
 *
 * The breakdown of calculated tax returned, includes:
 * + `taxSummary`: The overall total tax calculated.
 * + `taxSummary.aggregatedTaxBreakdown`: The total tax calculated for each jurisdiction.
 * + `lineItemTaxDetails.taxSummary`: The total tax calculated for each line item.
 * + `lineItemTaxDetails.taxBreakdown`: The tax calculated for each line item in each jurisdiction.
 */
export function calculateTax(payload: object): RequestOptionsFactory<any> {
  function __calculateTax({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.gateways.billing.v1.tax_calculation_gateway',
      method: 'POST' as any,
      methodFqn: 'wix.billing.v1.TaxCalculation.CalculateTax',
      packageName: PACKAGE_NAME,
      url: resolveWixBillingV1TaxCalculationUrl({
        protoPath: '/v1/calculate-tax',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __calculateTax;
}

/**
 * Retrieves a list of installed tax calculators.
 *
 * Wix uses these calculators to calculate tax.
 */
export function listTaxCalculators(
  payload: object
): RequestOptionsFactory<any> {
  function __listTaxCalculators({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.gateways.billing.v1.tax_calculation_gateway',
      method: 'GET' as any,
      methodFqn: 'wix.billing.v1.TaxCalculation.ListTaxCalculators',
      packageName: PACKAGE_NAME,
      url: resolveWixBillingV1TaxCalculationUrl({
        protoPath: '/v1/list-tax-calculators',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listTaxCalculators;
}
